import React, { useState, useEffect, useMemo, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { FilterMatchMode } from 'primereact/api';
import { Toast } from 'primereact/toast';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { OverlayPanel } from 'primereact/overlaypanel';
import { useSelector, useDispatch } from 'react-redux';
import { dictionary } from '../../dictionary';
import { fetchEspPosts, fetchInvPosts, fetchAllPosts } from '../../redux/thunk/thunk.js';
import InvoiceDialog from './utils/InvoiceDialog.js';
import { calculateGJ, countriesList, expandAll, collapseAll } from './utils/utils.js';
import { FilterService } from 'primereact/api';
import InvoiceReportDialog from './utils/InvoiceReportDialog.js';
import FilteredSummaryDialog from './utils/FilteredSummaryDialog.js';
import PriceListDialog from './utils/PriceListDialog.js';
import FilteredInvoicesDialog from './utils/FilteredInvoicesDialog.js';

import {
  clearDate,
  setClient,
  setComment,
  setCty,
  setDate,
  setEsp,
  setHour,
  setModType,
  setModule,
  setStage,
  setTask,
  setWpl,
  setLang,
  setVersion,
} from '../../redux/actions/logActions';
import { showEspDialog } from '../../redux/actions/dialogActions.js';

FilterService.register('countryMatch', (value, filterValue) => {
  if (filterValue === undefined || filterValue === null || filterValue.trim() === '') {
    return true;
  }
  if (value === undefined || value === null) {
    return false;
  }

  const searchTerm = String(filterValue).toLowerCase().trim();
  const countryNameTable = String(value).toLowerCase().trim();
  const foundCountry = countriesList.find((c) => c.name.toLowerCase() === countryNameTable);
  const countryCode = foundCountry && foundCountry.code ? foundCountry.code.toLowerCase() : '';

  return countryNameTable.startsWith(searchTerm) || countryCode.startsWith(searchTerm);
});

const isDevelopment = process.env.NODE_ENV === 'development';
const API_URL = isDevelopment
  ? process.env.REACT_APP_API_URL
  : 'https://eld-timer-backend-service.api.prod.sit.sys.odj.cloud/';

const isDateInRange = (dateString, start, end) => {
  if (
    !dateString ||
    typeof dateString === 'boolean' ||
    dateString === 'true' ||
    dateString === 'false' ||
    dateString === '-'
  )
    return false;
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return false;
  return d >= start && d <= end;
};

const EspLogsTable = (props) => {
  const { setPostEditing, setEditId } = props.contentProps;
  const dispatch = useDispatch();
  const espPosts = useSelector((state) => state.esps.data);
  const invPosts = useSelector((state) => state.invs.data);
  const allPosts = useSelector((state) => state.allPosts?.data || state.posts?.data || []);
  const toast = useRef(null);
  const popoverRef = useRef(null);
  const [totalRecords] = useState(0);
  const [recordsPerPage] = useState(10);
  const [expandedRows, setExpandedRows] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [showInvoice, setShowInvoice] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [invEdit, setInvEdit] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [globalFilteredData, setGlobalFilteredData] = useState(null);
  const [showFilteredDialog, setShowFilteredDialog] = useState(false);
  const [showPriceList, setShowPriceList] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [isSignOffFilterActive, setIsSignOffFilterActive] = useState(false);
  const [isProvideContentFilterActive, setIsProvideContentFilterActive] = useState(false);
  const [invoiceDateRange, setInvoiceDateRange] = useState(null);
  const [showInvoiceDateFilter, setShowInvoiceDateFilter] = useState(false);
  const [showFilteredFvDialog, setShowFilteredFvDialog] = useState(false);

  const [filters, setFilters] = useState({
    _computedStatus: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    _computedGJ: { value: null, matchMode: FilterMatchMode.EQUALS },
    esp: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    country: { value: null, matchMode: 'countryMatch' },
    module: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    lang: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    _invoices: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const isAnyFilterActive = useMemo(() => {
    const hasColFilter = Object.values(filters).some(
      (f) => f.value !== null && f.value !== '' && f.value !== undefined
    );
    const hasDateFilter = dateRange && dateRange[0] !== null;
    const hasInvoiceDateFilter = invoiceDateRange && invoiceDateRange[0] !== null;

    return (
      hasColFilter || hasDateFilter || isSignOffFilterActive || isProvideContentFilterActive || hasInvoiceDateFilter
    );
  }, [filters, dateRange, invoiceDateRange, isSignOffFilterActive, isProvideContentFilterActive]);

  const handleReportExport = () => {
    setShowReport(true);
  };

  const handleCreateGoogleSheet = (reportData) => {
    const now = new Date();
    const dateStr =
      now.getFullYear() + String(now.getMonth() + 1).padStart(2, '0') + String(now.getDate()).padStart(2, '0');
    const fileName = `eld_fv_report_${dateStr}`;

    let mdText = `# ${fileName}\n\nData:\n\n`;
    mdText += '| LP | ITEM NAME | VALUE | DESCRIPTION |\n';
    mdText += '| :--- | :--- | :--- | :--- |\n';

    reportData.forEach((item) => {
      const name = item.name || '';
      const desc = item.desc || '';

      if (Array.isArray(item.value)) {
        item.value.forEach((v, index) => {
          const safeLabel = String(v.label).replace(/\|/g, '').replace(/\n/g, ' ');
          const safeValue = String(v.value).replace(/\|/g, '').replace(/\n/g, ' ');

          if (index === 0) {
            mdText += `| ${item.id} | **${name}** | ${safeLabel}: ${safeValue} | ${desc} |\n`;
          } else {
            mdText += `| | | ${safeLabel}: ${safeValue} | |\n`;
          }
        });
      } else {
        const safeVal = String(item.value).replace(/\|/g, '').replace(/\n/g, ' ');
        mdText += `| ${item.id} | **${name}** | ${safeVal} | ${desc} |\n`;
      }
    });

    navigator.clipboard
      .writeText(mdText)
      .then(() => {
        const gemUrl = 'https://gemini.google.com/gem/16HBo00jKmoq8NCb4AKE1p3DViq8LKVyQ?usp=sharing';
        window.open(gemUrl, '_blank');

        alert(
          `Data copied to clipboard!\n\nTarget filename: ${fileName}\n\nPaste the data (Ctrl+V) into Gemini to generate the report.`
        );
      })
      .catch((err) => {
        console.error('Copy error:', err);
      });
  };

  const onFilter = (event) => {
    setFilters(event.filters);
  };

  const onPage = (rowData) => {
    setCurrentPage(rowData.first / recordsPerPage);
  };

  const handleEspRowEdit = (rowData) => {
    setPostEditing(true);
    setEditId(rowData._id);

    if (rowData.date) {
      let parts = rowData.date.split('-');
      const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2])).toDateString();
      dispatch(setDate(date));
    } else {
      dispatch(clearDate());
    }

    dispatch(setClient(rowData.client || 'esp'));
    dispatch(setEsp(rowData.esp || ''));
    dispatch(setCty(rowData.country || ''));
    dispatch(setLang(rowData.lang || ''));
    dispatch(setModule(rowData.module || ''));
    dispatch(setModType(rowData.module_type || ''));
    dispatch(setStage(rowData.stage || ''));
    dispatch(setHour(rowData.hours || 0));
    dispatch(setWpl(rowData.wpl || ''));
    dispatch(setComment(rowData.comment || ''));
    dispatch(setTask(rowData.task || ''));
    dispatch(setVersion(rowData.version || '1.0'));
    dispatch(showEspDialog());
  };

  const currentGJ = calculateGJ(new Date().toISOString());

  const currentGJSums = useMemo(() => {
    if (!invPosts || invPosts.length === 0) return { paid: 0, payable: 0 };

    let sumPaid = 0;
    let sumPayable = 0;

    invPosts.forEach((invoice) => {
      if (invoice.invoiceStatus === 'withdrawn') return;

      const amountToPay = Number(invoice.netPayable) || 0;
      if (amountToPay > 0) {
        sumPayable += amountToPay;
      }

      // if (invoice.dateInvoice1 && calculateGJ(invoice.dateInvoice1) === currentGJ) {
      //   sumPaid += Number(invoice.netPaid1Invoice) || 0;
      // }

      // if (invoice.dateInvoice2 && calculateGJ(invoice.dateInvoice2) === currentGJ) {
      //   sumPaid += Number(invoice.netPaid2Invoice) || 0;
      // }

      // ZMIANA tego co wyżej jest zakomentowane: Liczymy wpływy do paska górnego na podstawie daty płatności
      if (invoice.payment1Confirmed && calculateGJ(invoice.payment1Confirmed) === currentGJ) {
        sumPaid += Number(invoice.netPaid1Invoice) || 0;
      }

      if (invoice.payment2Confirmed && calculateGJ(invoice.payment2Confirmed) === currentGJ) {
        sumPaid += Number(invoice.netPaid2Invoice) || 0;
      }
    });

    return { paid: sumPaid, payable: sumPayable };
  }, [invPosts, currentGJ]);

  const tableData = useMemo(() => {
    if (!espPosts) return [];

    let filteredPosts = espPosts;

    if (isSignOffFilterActive || isProvideContentFilterActive) {
      filteredPosts = filteredPosts.filter((post) => {
        if (!post.entries || post.entries.length === 0) return false;

        const hasSignOff =
          isSignOffFilterActive &&
          post.entries.some((entry) => entry.stage === 'Sign_off' || entry.stage === 'Sign Off');
        const hasProvide =
          isProvideContentFilterActive && post.entries.some((entry) => entry.stage === 'Provide content');

        return hasSignOff || hasProvide;
      });
    }

    let invStart = null;
    let invEnd = null;
    if (invoiceDateRange && invoiceDateRange[0]) {
      invStart = new Date(invoiceDateRange[0]);
      invStart.setHours(0, 0, 0, 0);
      invEnd = invoiceDateRange[1] ? new Date(invoiceDateRange[1]) : new Date(invoiceDateRange[0]);
      invEnd.setHours(23, 59, 59, 999);
    }

    let mappedPosts = filteredPosts.map((post) => {
      let currentStatus = 'incomplete';
      let invoiceDate1 = null;
      let invoiceDate2 = null;
      let invNum1 = '';
      let invNum2 = '';
      let passesInvoiceFilter = true;

      if (invStart && invEnd) {
        passesInvoiceFilter = false;
      }

      if (post.entries && post.entries.length > 0) {
        const firstLogId = post.entries[0]._id;
        const matchingInvoice = invPosts.find((invoice) => invoice.entries.some((entry) => entry._id === firstLogId));

        if (matchingInvoice) {
          currentStatus = matchingInvoice.invoiceStatus || 'incomplete';

          let inv1Matches = true;
          let inv2Matches = true;

          if (invStart && invEnd) {
            // ZMIANA: Szukamy tylko po dacie potwierdzenia płatności (Payment Confirmed)
            inv1Matches = isDateInRange(matchingInvoice.payment1Confirmed, invStart, invEnd);
            inv2Matches = isDateInRange(matchingInvoice.payment2Confirmed, invStart, invEnd);

            passesInvoiceFilter = inv1Matches || inv2Matches;
          }

          if (inv1Matches) {
            invoiceDate1 = matchingInvoice.dateInvoice1;
            if (matchingInvoice.invoiceNumber1 && matchingInvoice.invoiceNumber1.trim() !== '') {
              invNum1 = matchingInvoice.invoiceNumber1.trim();
            }
          }

          if (inv2Matches) {
            invoiceDate2 = matchingInvoice.dateInvoice2;
            if (matchingInvoice.invoiceNumber2 && matchingInvoice.invoiceNumber2.trim() !== '') {
              invNum2 = matchingInvoice.invoiceNumber2.trim();
            }
          }
        }
      }

      let combinedInvoices = [invNum1, invNum2].filter(Boolean).join(' / ');
      if (combinedInvoices === '') {
        combinedInvoices = '-';
      }

      return {
        ...post,
        _computedStatus: currentStatus,
        _computedGJ: calculateGJ(invoiceDate1 || invoiceDate2),
        _invoices: combinedInvoices,
        _invoiceDate1: invoiceDate1,
        _invoiceDate2: invoiceDate2,
        _passesInvoiceFilter: passesInvoiceFilter,
      };
    });

    if (invStart && invEnd) {
      mappedPosts = mappedPosts.filter((p) => p._passesInvoiceFilter);
    }

    if (dateRange && dateRange[0]) {
      const start = new Date(dateRange[0]);
      start.setHours(0, 0, 0, 0);
      const end = dateRange[1] ? new Date(dateRange[1]) : new Date(dateRange[0]);
      end.setHours(23, 59, 59, 999);

      mappedPosts = mappedPosts.filter((post) => {
        const d1 = post._invoiceDate1 ? new Date(post._invoiceDate1) : null;
        const d2 = post._invoiceDate2 ? new Date(post._invoiceDate2) : null;

        const isD1InRange = d1 && d1 >= start && d1 <= end;
        const isD2InRange = d2 && d2 >= start && d2 <= end;

        return isD1InRange || isD2InRange;
      });
    }

    const activePosts = [];
    const withdrawnPosts = [];

    mappedPosts.forEach((post) => {
      if (post._computedStatus === 'withdrawn') {
        withdrawnPosts.push(post);
      } else {
        activePosts.push(post);
      }
    });

    return [...activePosts, ...withdrawnPosts];
  }, [espPosts, invPosts, dateRange, invoiceDateRange, isSignOffFilterActive, isProvideContentFilterActive]);

  const filteredInvoicesForReports = useMemo(() => {
    if (!invoiceDateRange || !invoiceDateRange[0]) return invPosts;

    let invStart = new Date(invoiceDateRange[0]);
    invStart.setHours(0, 0, 0, 0);
    let invEnd = invoiceDateRange[1] ? new Date(invoiceDateRange[1]) : new Date(invoiceDateRange[0]);
    invEnd.setHours(23, 59, 59, 999);

    return invPosts.map((inv) => {
      // ZMIANA: Zawężenie raportów tylko do daty potwierdzenia płatności
      const inv1Matches = isDateInRange(inv.payment1Confirmed, invStart, invEnd);
      const inv2Matches = isDateInRange(inv.payment2Confirmed, invStart, invEnd);

      if (inv1Matches && inv2Matches) return inv;

      const modifiedInv = { ...inv };

      if (!inv1Matches) {
        modifiedInv.dateInvoice1 = null;
        modifiedInv.invoiceNumber1 = '';
        modifiedInv.netPaid1Invoice = 0;
        modifiedInv.grossPaid1Invoice = 0;
        modifiedInv.payment1Confirmed = null;
      }
      if (!inv2Matches) {
        modifiedInv.dateInvoice2 = null;
        modifiedInv.invoiceNumber2 = '';
        modifiedInv.netPaid2Invoice = 0;
        modifiedInv.grossPaid2Invoice = 0;
        modifiedInv.payment2Confirmed = null;
      }

      return modifiedInv;
    });
  }, [invPosts, invoiceDateRange]);

  const hasColFilter = Object.values(filters).some((f) => f.value !== null && f.value !== '' && f.value !== undefined);

  useEffect(() => {
    if (!hasColFilter) {
      setGlobalFilteredData(tableData);
    }
    // eslint-disable-next-line
  }, [tableData, hasColFilter]);

  const header = (
    <div className="header-btn-wrapper">
      <div className="expand-btn-wrapper">
        <Button
          icon="pi pi-plus"
          label={dictionary.user_logs_table_btn_label_expand}
          onClick={() => expandAll(tableData, setExpandedRows)}
        />
        <Button
          icon="pi pi-minus"
          label={dictionary.user_logs_table_btn_label_collapse}
          onClick={() => collapseAll(setExpandedRows)}
        />
      </div>

      <div className="gj-sum-wrapper">
        <Button
          label={dictionary.esp_price_list_btn || 'Price list'}
          icon="pi pi-tags"
          className="p-button-outlined p-button-info"
          onClick={() => setShowPriceList(true)}
        />
        <Button
          icon="pi pi-table"
          label={dictionary.esp_dialog_invoice_filtered_fv || 'FILTERED INVOICES'}
          onClick={() => setShowFilteredFvDialog(true)}
          disabled={!(invoiceDateRange && invoiceDateRange[0])}
          className={`btn-filtered-tab ${invoiceDateRange && invoiceDateRange[0] ? 'p-button-info' : 'p-button-secondary'}`}
        />
        <Button
          icon="pi pi-table"
          label={dictionary.esp_dialog_invoice_filtered_tab}
          onClick={() => setShowFilteredDialog(true)}
          disabled={!isAnyFilterActive}
          className={`btn-filtered-tab ${isAnyFilterActive ? 'p-button-info' : 'p-button-secondary'}`}
        />
        <Button icon="pi pi-file-export" label={dictionary.esp_dialog_invoice_export} onClick={handleReportExport} />
        <div>
          <span>GJ{currentGJ} Paid: </span>
          <span>{currentGJSums.paid.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} PLN</span>
        </div>
        <div>
          <span>To be paid: </span>
          <span className="paid-warn">
            {currentGJSums.payable.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} PLN
          </span>
        </div>
      </div>
    </div>
  );

  const issueClass = (status) => {
    switch (status) {
      case 'incomplete':
        return 'status-red';
      case 'in-progress':
        return 'status-yellow';
      case 'complete':
        return 'status-green';
      case 'withdrawn':
        return 'status-blue';
      default:
        return 'status-red';
    }
  };

  const showToastMsg = (severity = 'success', detail = dictionary.esp_dialog_invoice_toast_detail) => {
    toast.current?.show({
      severity: severity,
      summary: severity === 'success' ? dictionary.toast_summary_success : 'Error',
      detail: detail,
    });
  };

  const rowExpansionTemplate = (rowData) => {
    return (
      <>
        <DataTable value={rowData.entries} stripedRows className="expanded-table">
          <Column field="user" header="User"></Column>
          <Column field="client" header="Client"></Column>
          <Column field="esp" header="Esp"></Column>
          <Column field="country" header="Country"></Column>
          <Column field="module_type" header="Module type"></Column>
          <Column field="module" header="Module"></Column>
          <Column field="stage" header="Stage"></Column>
          <Column field="hours" header="Hours"></Column>
          <Column field="comment" header="Comment"></Column>
          <Column field="date" header="Date"></Column>
          <Column
            body={(entry) => (
              <Button
                icon="pi pi-pencil"
                rounded
                text
                severity="warning"
                onClick={() => handleEspRowEdit(entry)}
                aria-label="Edit"
              />
            )}
          />
        </DataTable>
      </>
    );
  };

  const findInvoiceIdByLog = (rowData) => {
    if (!rowData.entries || rowData.entries.length === 0) return null;
    const firstLogId = rowData.entries[0]._id;

    const matchingInvoice = invPosts.find((invoice) => {
      return invoice.entries.some((entry) => entry._id === firstLogId);
    });

    return matchingInvoice ? matchingInvoice._id : null;
  };

  const handleInvoice = async (rowData) => {
    const invoiceId = findInvoiceIdByLog(rowData);

    if (!invoiceId) {
      console.warn('No existing invoice found for this set of logs. Proceeding to new invoice creation.');
      setInvEdit(false);
      setInvoiceData(rowData);
      setShowInvoice(true);
      return;
    }
    try {
      const response = await fetch(`${API_URL}invoices/${invoiceId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Błąd HTTP: ${response.status}`);
      }
      const invoiceDetails = await response.json();
      invoiceDetails.entries = rowData.entries;
      setInvEdit(true);
      setInvoiceData(invoiceDetails);
      setShowInvoice(true);
    } catch (error) {
      console.error('Error fetching invoice details:', error);
    }
  };

  useEffect(() => {
    const isAdmin = sessionStorage.getItem('isAdmin') === 'true';

    if (isAdmin) {
      if (espPosts.length < 1) {
        dispatch(fetchEspPosts());
      }
      if (invPosts.length < 1) {
        dispatch(fetchInvPosts());
      }
      if (allPosts.length < 1) {
        dispatch(fetchAllPosts());
      }
    }
  }, [dispatch, espPosts.length, invPosts.length, allPosts.length]);

  const actionHeaderTemplate = (
    <div className="date-filter-header">
      <Button
        type="button"
        icon="pi pi-filter"
        label={dictionary.esp_more_filters}
        rounded
        text
        severity="info"
        onClick={(e) => popoverRef.current.toggle(e)}
        tooltip="Show advanced filters"
        tooltipOptions={{ position: 'bottom' }}
      />
    </div>
  );

  return (
    <div className="esp-table">
      <Toast ref={toast} />
      <OverlayPanel ref={popoverRef}>
        <div className="more-filters-wrapper">
          {!isSignOffFilterActive ? (
            <Button
              label={dictionary.esp_sign_off_stage_filter || 'Sign off stage filter'}
              icon="pi pi-verified"
              size="small"
              className="p-button-secondary"
              onClick={() => {
                setIsSignOffFilterActive(true);
                popoverRef.current.hide();
              }}
            />
          ) : (
            <Button
              label={dictionary.esp_sign_off_stage_filter_clear || 'Clear Sign off filter'}
              icon="pi pi-filter-slash"
              size="small"
              severity="danger"
              text
              onClick={() => {
                setIsSignOffFilterActive(false);
                popoverRef.current.hide();
              }}
            />
          )}

          <hr className="more-filters-divider" />

          {!isProvideContentFilterActive ? (
            <Button
              label="Provide content filter"
              icon="pi pi-file-plus"
              size="small"
              className="p-button-secondary"
              onClick={() => {
                setIsProvideContentFilterActive(true);
                popoverRef.current.hide();
              }}
            />
          ) : (
            <Button
              label="Clear Provide content filter"
              icon="pi pi-filter-slash"
              size="small"
              severity="danger"
              text
              onClick={() => {
                setIsProvideContentFilterActive(false);
                popoverRef.current.hide();
              }}
            />
          )}

          <hr className="more-filters-divider" />

          {!(dateRange && dateRange[0]) ? (
            <Button
              label={dictionary.esp_date_range_filter || 'Date range filter'}
              icon="pi pi-calendar"
              size="small"
              className="p-button-secondary"
              onClick={() => {
                setShowDateFilter(true);
                popoverRef.current.hide();
              }}
            />
          ) : (
            <Button
              label={dictionary.esp_date_range_filter_clear || 'Clear Date range filter'}
              icon="pi pi-filter-slash"
              size="small"
              severity="danger"
              text
              onClick={() => {
                setDateRange(null);
                popoverRef.current.hide();
              }}
            />
          )}
          <hr className="more-filters-divider" />

          {!(invoiceDateRange && invoiceDateRange[0]) ? (
            <Button
              label={dictionary.esp_invoice_date_filter || 'Invoice Date filter'}
              icon="pi pi-calendar-plus"
              size="small"
              className="p-button-secondary"
              onClick={() => {
                setShowInvoiceDateFilter(true);
                popoverRef.current.hide();
              }}
            />
          ) : (
            <Button
              label={dictionary.esp_invoice_date_filter_clear || 'Clear Invoice Date filter'}
              icon="pi pi-filter-slash"
              size="small"
              severity="danger"
              text
              onClick={() => {
                setInvoiceDateRange(null);
                popoverRef.current.hide();
              }}
            />
          )}
        </div>
      </OverlayPanel>
      <DataTable
        value={tableData}
        totalRecords={totalRecords}
        onPage={onPage}
        expandedRows={expandedRows}
        onRowToggle={(e) => {
          setExpandedRows(e.data);
        }}
        rowExpansionTemplate={rowExpansionTemplate}
        stripedRows
        paginator
        header={header}
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} logs"
        rows={recordsPerPage}
        first={currentPage * recordsPerPage}
        removableSort
        filters={filters}
        onFilter={onFilter}
        onValueChange={(filteredData) => setGlobalFilteredData(filteredData)}
        filterDisplay="row"
        emptyMessage="No logs found."
      >
        <Column expander style={{ width: '5rem' }} />
        <Column
          field="_computedStatus"
          header="Status"
          filter
          body={(rowData) => (
            <div className="status-btn-wrapper">
              <div className={`status-button ${issueClass(rowData._computedStatus)}`}></div>
            </div>
          )}
        ></Column>
        <Column field="_computedGJ" header="GJ" filter></Column>
        <Column field="esp" header="ESP" filter></Column>
        <Column field="country" header="Country" filter></Column>
        <Column field="module" header="Module" filter></Column>
        <Column field="lang" header="Language" filter></Column>
        <Column field="_invoices" header="Invoices" filter></Column>
        <Column field="version" header="Version"></Column>
        <Column field="totalHours" header="Total Hours"></Column>
        <Column field="date" header="Last changes"></Column>
        <Column
          header={actionHeaderTemplate}
          body={(rowData) => (
            <div className="row-btn-wrapper">
              <Button icon="pi pi-file-edit" rounded text severity="warning" onClick={() => handleInvoice(rowData)} />
            </div>
          )}
        ></Column>
      </DataTable>
      <Dialog
        header="Select Date Range"
        visible={showDateFilter}
        onHide={() => setShowDateFilter(false)}
        resizable={false}
        className="date-filter-dialog"
      >
        <div className="date-filter-content">
          <Calendar
            value={dateRange}
            onChange={(e) => setDateRange(e.value)}
            selectionMode="range"
            readOnlyInput
            inline
            className="date-filter-calendar"
          />
        </div>
      </Dialog>
      <Dialog
        header={dictionary.esp_invoice_date_filter_header}
        visible={showInvoiceDateFilter}
        onHide={() => setShowInvoiceDateFilter(false)}
        resizable={false}
        className="date-filter-dialog"
      >
        <div className="date-filter-content">
          <Calendar
            value={invoiceDateRange}
            onChange={(e) => setInvoiceDateRange(e.value)}
            selectionMode="range"
            readOnlyInput
            inline
            className="date-filter-calendar"
          />
        </div>
      </Dialog>
      {invoiceData && (
        <InvoiceDialog
          showInvoice={showInvoice}
          setShowInvoice={setShowInvoice}
          invoiceData={invoiceData}
          setInvoiceData={setInvoiceData}
          invEdit={invEdit}
          setInvEdit={setInvEdit}
          showToast={showToastMsg}
        />
      )}
      <FilteredSummaryDialog
        visible={showFilteredDialog}
        onHide={() => setShowFilteredDialog(false)}
        data={hasColFilter ? globalFilteredData : tableData}
        allInvoices={filteredInvoicesForReports}
        currentGJ={filters._computedGJ.value || currentGJ}
      />
      <InvoiceReportDialog
        visible={showReport}
        onHide={() => setShowReport(false)}
        data={hasColFilter ? globalFilteredData : tableData}
        allInvoices={invPosts}
        filteredInvoices={filteredInvoicesForReports}
        allPosts={allPosts}
        currentGJ={filters._computedGJ.value || currentGJ}
        dateRange={dateRange}
        onCreateGoogleSheet={handleCreateGoogleSheet}
      />
      <FilteredInvoicesDialog
        visible={showFilteredFvDialog}
        onHide={() => setShowFilteredFvDialog(false)}
        data={hasColFilter ? globalFilteredData : tableData}
        allInvoices={filteredInvoicesForReports}
      />
      <PriceListDialog visible={showPriceList} onHide={() => setShowPriceList(false)} />
    </div>
  );
};

export default EspLogsTable;
